import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Select from 'react-select';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  format, parseISO, subDays, formatISO, startOfDay, endOfDay,
  eachDayOfInterval, startOfWeek, endOfWeek, startOfMonth, endOfMonth,
  isWithinInterval, getUnixTime, getHours // Ensure getHours is imported
} from 'date-fns';
import { toast } from 'react-toastify';

import { getConsolidatedAlertsForAnalytics, getGeminiTokens } from '../Api';
import './AnalyticsDashboard.css';

// Configuration for identifying specific alerts from consolidated data
const ALERT_CONFIG = {
  AUDIO_NEGATIVE: {
    mode: 'audio', 
    isMatch: (alert) => typeof alert.alert_reason === 'string' && alert.alert_reason.toLowerCase().includes('alert'),
    label: 'Audio Alert'
  },
  BOUNCE_OCCURRED: {
    mode: 'bounce', 
    isMatch: (alert) => typeof alert.alert_reason === 'string' && (alert.alert_reason.toLowerCase().includes('bounce') || alert.alert_reason.toLowerCase().includes('alert')),
    label: 'Bounce Occurred/Risk'
  },
  LONG_IDLE: { 
    mode: 'idle', 
    isMatch: (alert, threshold = 300) => { 
        if (alert && alert.mode && alert.mode.toLowerCase() === 'idle') {
            const duration = ALERT_CONFIG.LONG_IDLE.getDuration(alert); 
            return typeof duration === 'number' && duration >= threshold;
        }
        return false; 
    },
    label: 'Long Idle (>5min)',
    getDuration: (alert) => { 
        if (alert && alert.mode && alert.mode.toLowerCase() === 'idle' && typeof alert.alert_reason === 'string') {
            const reasonLower = alert.alert_reason.toLowerCase();
            let match = reasonLower.match(/(\d+)\s*m\s*(\d+)\s*s/);
            if (match && match.length === 3) {
                const minutes = parseInt(match[1], 10);
                const seconds = parseInt(match[2], 10);
                return (minutes * 60) + seconds;
            }
            match = reasonLower.match(/(\d+)\s*m/);
            if (match && match.length === 2 && !reasonLower.includes('s')) { 
                return parseInt(match[1], 10) * 60;
            }
            match = reasonLower.match(/(\d+)\s*s/);
            if (match && match.length === 2 && !reasonLower.includes('m')) { 
                return parseInt(match[1], 10);
            }
        }
        if (alert && alert.details && typeof alert.details.idle_duration_seconds === 'number') {
            return alert.details.idle_duration_seconds;
        }
        return null; 
    }
  },
  LONG_WAIT: {
    mode: 'waiting_time',
    isMatch: (alert, threshold = 300) => {
      if (alert && alert.mode && alert.mode.toLowerCase() === 'waiting_time') {
        const waitTime = ALERT_CONFIG.LONG_WAIT.getWaitTime(alert);
        return typeof waitTime === 'number' && waitTime >= threshold;
      }
      return false;
    },
    label: 'Long Wait Time (>5min)',
    getWaitTime: (alert) => {
      if (alert && alert.details && typeof alert.details.waiting_time_seconds === 'number') {
        return alert.details.waiting_time_seconds;
      }
      if (alert && typeof alert.alert_reason === 'string') {
        const reasonLower = alert.alert_reason.toLowerCase();
        let match = reasonLower.match(/(\d+)\s*m\s*(\d+)\s*s/);
        if (match && match.length === 3) {
          const minutes = parseInt(match[1], 10);
          const seconds = parseInt(match[2], 10);
          return (minutes * 60) + seconds;
        }
        match = reasonLower.match(/(\d+)\s*m/);
        if (match && match.length === 2 && !reasonLower.includes('s')) {
          return parseInt(match[1], 10) * 60;
        }
        match = reasonLower.match(/(\d+)\s*s/);
        if (match && match.length === 2 && !reasonLower.includes('m')) {
          return parseInt(match[1], 10);
        }
      }
      return null;
    }
  },
  CROWD_WARNING: {
    mode: 'waiting_time',
    isMatch: (alert) => {
      return alert && alert.mode && alert.mode.toLowerCase() === 'waiting_time' && 
             typeof alert.alert_reason === 'string' && 
             alert.alert_reason.toLowerCase().includes('crowd');
    },
    label: 'Crowd Warning'
  }
};

const AnalyticsDashboard = () => {
  const [rawConsolidatedAlerts, setRawConsolidatedAlerts] = useState([]);
  
  const [startDate, setStartDate] = useState(startOfDay(new Date()));
  const [endDate, setEndDate] = useState(endOfDay(new Date()));
  const [granularity, setGranularity] = useState('daily');

  const [selectedAnalyticsModes, setSelectedAnalyticsModes] = useState([]);
  const [selectedAnalyticsCameras, setSelectedAnalyticsCameras] = useState([]);
  const [availableAnalyticsModes, setAvailableAnalyticsModes] = useState([]);
  const [availableAnalyticsCameras, setAvailableAnalyticsCameras] = useState([]);

  const [alertTrendData, setAlertTrendData] = useState([]);
  const [idleAverageData, setIdleAverageData] = useState([]);
  const [idleDistributionData, setIdleDistributionData] = useState([]);
  const [topCamerasData, setTopCamerasData] = useState([]); 
  const [alertsByHourData, setAlertsByHourData] = useState([]); 
  const [geminiTokensData, setGeminiTokensData] = useState(null); 

  const [isLoading, setIsLoading] = useState(false); 
  const [isProcessing, setIsProcessing] = useState(false); 
  const [error, setError] = useState(null);

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailModalContent, setDetailModalContent] = useState({ date: null, seriesName: null, alerts: [], hourlyStats: [] });

  const isoStartDate = useMemo(() => startDate ? formatISO(startOfDay(startDate)) : null, [startDate]);
  const isoEndDate = useMemo(() => endDate ? formatISO(endOfDay(endDate)) : null, [endDate]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const fetchAlertData = async () => {
      if (!isoStartDate || !isoEndDate) return;
      if (startDate && endDate && parseISO(isoStartDate) > parseISO(isoEndDate)) {
        toast.error("Start date cannot be after end date.");
        return;
      }
      setIsLoading(true);
      setIsProcessing(true); 
      setError(null);
      setRawConsolidatedAlerts([]);
      try {
        const response = await getConsolidatedAlertsForAnalytics(isoStartDate, isoEndDate, 'desc'); 
        // Handle both new paginated response and legacy array response (backward compatibility)
        const alerts = response.items || response || [];
        setRawConsolidatedAlerts(alerts);
        if (!alerts || alerts.length === 0) {
          if (!toast.isActive("noDataToast")) { 
             toast.info("No consolidated alerts found for the selected period.", {toastId: "noDataToast"});
          }
        }
      } catch (err) {
        console.error("Failed to load consolidated alerts:", err);
        setError('Could not load consolidated alerts. ' + err.message);
        toast.error('Failed to load consolidated alerts: ' + err.message);
      } finally {
        setIsLoading(false); 
      }
    };
    fetchAlertData();
  }, [isoStartDate, isoEndDate]);

  useEffect(() => {
    const fetchGeminiTokens = async () => {
      try {
        const tokens = await getGeminiTokens();
        setGeminiTokensData(tokens);
      } catch (err) {
        console.error("Failed to load gemini tokens:", err);
        // Don't show error toast for token data as it's supplementary data
      }
    };
    fetchGeminiTokens();
  }, []); // No dependencies since API doesn't require date parameters

  useEffect(() => {
    if (rawConsolidatedAlerts.length > 0) {
      const uniqueModes = [...new Set(rawConsolidatedAlerts.map(a => a.mode).filter(Boolean))]
        .map(m => ({ value: m.toLowerCase(), label: m.charAt(0).toUpperCase() + m.slice(1).toLowerCase() }))
        .sort((a,b) => a.label.localeCompare(b.label));
      setAvailableAnalyticsModes(uniqueModes);

      const uniqueCameras = [...new Set(rawConsolidatedAlerts.map(a => a.camera_display_name).filter(Boolean))]
        .map(c => ({ value: c, label: c }))
        .sort((a,b) => a.label.localeCompare(b.label));
      setAvailableAnalyticsCameras(uniqueCameras);
    } else {
      setAvailableAnalyticsModes([]);
      setAvailableAnalyticsCameras([]);
    }
  }, [rawConsolidatedAlerts]);

  // Process gemini tokens data for chart visualization
  const processedTokenData = useMemo(() => {
    if (!geminiTokensData || !geminiTokensData.models) {
      return {
        chartData: [],
        totalInputTokens: 0,
        totalOutputTokens: 0,
        totalTokens: 0
      };
    }

    const models = geminiTokensData.models;
    const chartData = Object.entries(models).map(([modelName, tokens]) => ({
      model: modelName,
      input_tokens: tokens.input_tokens || 0,
      output_tokens: tokens.output_tokens || 0,
      total_tokens: (tokens.input_tokens || 0) + (tokens.output_tokens || 0)
    }));

    const totalInputTokens = chartData.reduce((sum, model) => sum + model.input_tokens, 0);
    const totalOutputTokens = chartData.reduce((sum, model) => sum + model.output_tokens, 0);
    const totalTokens = totalInputTokens + totalOutputTokens;

    return {
      chartData,
      totalInputTokens,
      totalOutputTokens,
      totalTokens
    };
  }, [geminiTokensData]);

  useEffect(() => {
    if (!rawConsolidatedAlerts) {
        setAlertTrendData([]); setIdleAverageData([]); setIdleDistributionData([]); 
        setTopCamerasData([]); setAlertsByHourData([]);
        setIsProcessing(false);
        return;
    }
    
    setIsProcessing(true);

    let filteredAlertsForProcessing = [...rawConsolidatedAlerts];

    if (selectedAnalyticsModes.length > 0) {
      const modeValues = selectedAnalyticsModes.map(m => m.value);
      filteredAlertsForProcessing = filteredAlertsForProcessing.filter(alert =>
        alert.mode && modeValues.includes(alert.mode.toLowerCase())
      );
    }
    if (selectedAnalyticsCameras.length > 0) {
      const cameraValues = selectedAnalyticsCameras.map(c => c.value);
      filteredAlertsForProcessing = filteredAlertsForProcessing.filter(alert =>
        alert.camera_display_name && cameraValues.includes(alert.camera_display_name)
      );
    }
    
    if (filteredAlertsForProcessing.length === 0) {
        setAlertTrendData([]); setIdleAverageData([]); setIdleDistributionData([]);
        setTopCamerasData([]); setAlertsByHourData([]);
        setIsProcessing(false);
        if(!isLoading && rawConsolidatedAlerts.length > 0 && !toast.isActive("filterNoMatchToast")) {
             toast.info("No alerts match the selected Mode/Camera filters.", {toastId: "filterNoMatchToast"});
        }
        return;
    }

    const getDateKey = (dateString, currentGranularity) => {
      const d = parseISO(dateString);
      if (currentGranularity === 'daily') return format(d, 'yyyy-MM-dd');
      if (currentGranularity === 'weekly') return format(startOfWeek(d, { weekStartsOn: 1 }), 'yyyy-MM-dd');
      if (currentGranularity === 'monthly') return format(startOfMonth(d), 'yyyy-MM-dd');
      return format(d, 'yyyy-MM-dd');
    };

    const allDatePoints = {};
    if (startDate && endDate) {
      let intervalDates;
      if (granularity === 'daily') {
        intervalDates = eachDayOfInterval({ start: startDate, end: endDate });
      } else if (granularity === 'weekly') {
        let current = startOfWeek(startDate, { weekStartsOn: 1 });
        intervalDates = [];
        while (current <= endDate) {
          intervalDates.push(current);
          current = startOfWeek(subDays(endOfWeek(current, { weekStartsOn: 1 }), -1), { weekStartsOn: 1 });
        }
      } else { 
        let current = startOfMonth(startDate);
        intervalDates = [];
        while (current <= endDate) {
          intervalDates.push(current);
          current = startOfMonth(subDays(endOfMonth(current), -1));
        }
      }
      intervalDates.forEach(d => {
        const key = format(d, 'yyyy-MM-dd');
        allDatePoints[key] = {
          date: key,
          [ALERT_CONFIG.AUDIO_NEGATIVE.label]: 0,
          [ALERT_CONFIG.BOUNCE_OCCURRED.label]: 0,
          [ALERT_CONFIG.LONG_IDLE.label]: 0,
          total_idle_duration: 0,
          idle_event_count: 0,
        };
      });
    }

    const processedTrends = JSON.parse(JSON.stringify(allDatePoints));
    const idleDurations = [];
    const cameraCounts = {};
    // MODIFIED: hourlyAlertCounts initialization for mode breakdown
    const hourlyAlertCounts = Array(24).fill(null).map((_, i) => ({ 
        hour: `${String(i).padStart(2, '0')}:00`, 
        [ALERT_CONFIG.AUDIO_NEGATIVE.label]: 0,
        [ALERT_CONFIG.BOUNCE_OCCURRED.label]: 0,
        [ALERT_CONFIG.LONG_IDLE.label]: 0,
    }));


    filteredAlertsForProcessing.forEach(alert => {
      if (!alert || typeof alert.mode !== 'string' || typeof alert.event_timestamp_utc !== 'string') {
        console.warn("Analytics Processing: Skipping invalid alert object:", alert);
        return;
      }
      let alertTime;
      try { alertTime = parseISO(alert.event_timestamp_utc); } 
      catch (e) { console.warn("Analytics Processing: Failed to parse alert timestamp:", alert.event_timestamp_utc, alert); return; }

      // Process for trends (daily/weekly/monthly)
      if (isWithinInterval(alertTime, { start: startOfDay(startDate), end: endOfDay(endDate) })) {
        const dateKey = getDateKey(alert.event_timestamp_utc, granularity);
        if (processedTrends[dateKey]) {
          const alertModeLower = alert.mode.toLowerCase();
          if (alertModeLower === ALERT_CONFIG.AUDIO_NEGATIVE.mode && ALERT_CONFIG.AUDIO_NEGATIVE.isMatch(alert)) {
            processedTrends[dateKey][ALERT_CONFIG.AUDIO_NEGATIVE.label]++;
          }
          if (alertModeLower === ALERT_CONFIG.BOUNCE_OCCURRED.mode && ALERT_CONFIG.BOUNCE_OCCURRED.isMatch(alert)) {
            processedTrends[dateKey][ALERT_CONFIG.BOUNCE_OCCURRED.label]++;
          }
          if (alertModeLower === ALERT_CONFIG.LONG_IDLE.mode) {
            const duration = ALERT_CONFIG.LONG_IDLE.getDuration(alert);
            if (typeof duration === 'number') {
              processedTrends[dateKey].total_idle_duration += duration;
              processedTrends[dateKey].idle_event_count++;
              idleDurations.push(duration);
              if (ALERT_CONFIG.LONG_IDLE.isMatch(alert)) { 
                processedTrends[dateKey][ALERT_CONFIG.LONG_IDLE.label]++;
              }
            }
          }
        } else { 
            console.warn("Analytics Processing (Trends): dateKey not found.", { dateKey, alert });
        }
      }

      // Only count alerts that match ALERT_CONFIG criteria (consistent with other charts)
      if (alert.camera_display_name) {
        const alertModeLower = alert.mode.toLowerCase();
        if (alertModeLower === ALERT_CONFIG.AUDIO_NEGATIVE.mode && ALERT_CONFIG.AUDIO_NEGATIVE.isMatch(alert)) {
          cameraCounts[alert.camera_display_name] = (cameraCounts[alert.camera_display_name] || 0) + 1;
        } else if (alertModeLower === ALERT_CONFIG.BOUNCE_OCCURRED.mode && ALERT_CONFIG.BOUNCE_OCCURRED.isMatch(alert)) {
          cameraCounts[alert.camera_display_name] = (cameraCounts[alert.camera_display_name] || 0) + 1;
        } else if (alertModeLower === ALERT_CONFIG.LONG_IDLE.mode && ALERT_CONFIG.LONG_IDLE.isMatch(alert)) {
          cameraCounts[alert.camera_display_name] = (cameraCounts[alert.camera_display_name] || 0) + 1;
        } else if (alertModeLower === ALERT_CONFIG.LONG_WAIT.mode && ALERT_CONFIG.LONG_WAIT.isMatch(alert)) {
          cameraCounts[alert.camera_display_name] = (cameraCounts[alert.camera_display_name] || 0) + 1;
        } else if (alertModeLower === ALERT_CONFIG.CROWD_WARNING.mode && ALERT_CONFIG.CROWD_WARNING.isMatch(alert)) {
          cameraCounts[alert.camera_display_name] = (cameraCounts[alert.camera_display_name] || 0) + 1;
        }
      }

      // MODIFIED: Process for Alerts by Hour (with mode breakdown)
      const hour = getHours(alertTime); 
      if (hourlyAlertCounts[hour]) {
        const alertModeLower = alert.mode.toLowerCase();
        // We count specific alert types as defined by ALERT_CONFIG for consistency
        if (alertModeLower === ALERT_CONFIG.AUDIO_NEGATIVE.mode && ALERT_CONFIG.AUDIO_NEGATIVE.isMatch(alert)) {
            hourlyAlertCounts[hour][ALERT_CONFIG.AUDIO_NEGATIVE.label]++;
        } else if (alertModeLower === ALERT_CONFIG.BOUNCE_OCCURRED.mode && ALERT_CONFIG.BOUNCE_OCCURRED.isMatch(alert)) {
            hourlyAlertCounts[hour][ALERT_CONFIG.BOUNCE_OCCURRED.label]++;
        } else if (alertModeLower === ALERT_CONFIG.LONG_IDLE.mode && ALERT_CONFIG.LONG_IDLE.isMatch(alert)) { 
            hourlyAlertCounts[hour][ALERT_CONFIG.LONG_IDLE.label]++;
        }
      }
    });

    const finalAlertTrendData = Object.values(processedTrends)
      .map(d => ({
        date: d.date,
        audio_negative_count: d[ALERT_CONFIG.AUDIO_NEGATIVE.label],
        bounce_occurred_count: d[ALERT_CONFIG.BOUNCE_OCCURRED.label],
        long_idle_event_count: d[ALERT_CONFIG.LONG_IDLE.label]
      }))
      .sort((a,b) => getUnixTime(parseISO(a.date)) - getUnixTime(parseISO(b.date)));

    const finalIdleAverageData = Object.values(processedTrends)
      .map(d => ({
        date: d.date,
        average_idle_minutes: d.idle_event_count > 0 ? parseFloat((d.total_idle_duration / d.idle_event_count / 60).toFixed(1)) : 0,
      }))
      .sort((a,b) => getUnixTime(parseISO(a.date)) - getUnixTime(parseISO(b.date)));

    setAlertTrendData(finalAlertTrendData);
    setIdleAverageData(finalIdleAverageData);

    const buckets = { "0-1 min": 0, "1-5 min": 0, "5-10 min": 0, ">10 min": 0 };
    idleDurations.forEach(duration => {
      if (duration < 60) buckets["0-1 min"]++;
      else if (duration < 300) buckets["1-5 min"]++;
      else if (duration < 600) buckets["5-10 min"]++;
      else buckets[">10 min"]++;
    });
    setIdleDistributionData(Object.entries(buckets).map(([bucket, count]) => ({ bucket, count })));

    const topCamerasArray = Object.entries(cameraCounts)
      .map(([camera_display_name, count]) => ({ camera_display_name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10); 
    setTopCamerasData(topCamerasArray);

    setAlertsByHourData(hourlyAlertCounts);

    setIsProcessing(false);
  }, [rawConsolidatedAlerts, granularity, startDate, endDate, selectedAnalyticsModes, selectedAnalyticsCameras, isLoading]);

  const handleChartPointClick = useCallback((clickedDateString, seriesName) => {
    if (!clickedDateString) return;
    const clickedDate = parseISO(clickedDateString);

    let alertsForDay = rawConsolidatedAlerts.filter(alert => {
        if (!alert || !alert.event_timestamp_utc) return false;
        try {
            const alertDay = startOfDay(parseISO(alert.event_timestamp_utc));
            return getUnixTime(alertDay) === getUnixTime(startOfDay(clickedDate));
        } catch { return false; }
    });

    if (selectedAnalyticsModes.length > 0) {
      const modeValues = selectedAnalyticsModes.map(m => m.value);
      alertsForDay = alertsForDay.filter(alert =>
        alert.mode && modeValues.includes(alert.mode.toLowerCase())
      );
    }
    if (selectedAnalyticsCameras.length > 0) {
      const cameraValues = selectedAnalyticsCameras.map(c => c.value);
      alertsForDay = alertsForDay.filter(alert =>
        alert.camera_display_name && cameraValues.includes(alert.camera_display_name)
      );
    }
    
    const hourlyStats = {};
    for (let i = 0; i < 24; i++) { hourlyStats[format(new Date(2000,0,1,i), 'HH:00')] = { total: 0, audio: 0, bounce: 0, idle: 0 }; }

    alertsForDay.forEach(alert => {
        const hourKey = format(parseISO(alert.event_timestamp_utc), 'HH:00');
        if (hourlyStats[hourKey]) {
            hourlyStats[hourKey].total++; // Keep total for modal if needed
            const mode = alert.mode?.toLowerCase();
            if (mode === ALERT_CONFIG.AUDIO_NEGATIVE.mode && ALERT_CONFIG.AUDIO_NEGATIVE.isMatch(alert)) hourlyStats[hourKey].audio++;
            else if (mode === ALERT_CONFIG.BOUNCE_OCCURRED.mode && ALERT_CONFIG.BOUNCE_OCCURRED.isMatch(alert)) hourlyStats[hourKey].bounce++;
            else if (mode === ALERT_CONFIG.LONG_IDLE.mode && ALERT_CONFIG.LONG_IDLE.isMatch(alert)) hourlyStats[hourKey].idle++;
        }
    });
    const hourlyStatsArray = Object.entries(hourlyStats).map(([hour, counts]) => ({hour, ...counts}));

    setDetailModalContent({ 
        date: clickedDateString, 
        seriesName, 
        alerts: alertsForDay, 
        hourlyStats: hourlyStatsArray 
    });
    setShowDetailModal(true);
  }, [rawConsolidatedAlerts, selectedAnalyticsModes, selectedAnalyticsCameras]);

  const formatDateTick = (tickItem) => {
    try {
      if (!tickItem) return '';
      return format(parseISO(tickItem), granularity === 'monthly' ? 'MMM yyyy' : 'MMM d');
    } catch {
      return tickItem;
    }
  };

  const renderChart = (title, data, children, dataKeyForNoDataCheck = "date") => {
    const showLoadingOrProcessing = (isLoading || isProcessing) && (!data || data.length === 0 || !data.some(item => typeof item?.[dataKeyForNoDataCheck] !== 'undefined' && item?.[dataKeyForNoDataCheck] !== null));
    const showNoDataMessage = !(isLoading || isProcessing) && (!data || data.length === 0 || !data.some(item => typeof item?.[dataKeyForNoDataCheck] !== 'undefined' && item?.[dataKeyForNoDataCheck] !== null));

    if (showLoadingOrProcessing) return <div className="loading-message-analytics">Loading/Processing {title}...</div>;
    if (showNoDataMessage) return <div className="no-data-message-analytics">No data available for {title}.</div>;
    return ( <ResponsiveContainer width="100%" height={300}>{children}</ResponsiveContainer> );
  };

  const DailyDetailModal = ({ show, onClose, content }) => {
    if (!show || !content || !content.date) return null;
    const { date, seriesName, alerts, hourlyStats } = content;

    return (
      <div className="analytics-modal-overlay" onClick={onClose}>
        <div className="analytics-modal-content" onClick={(e) => e.stopPropagation()}>
          <button className="analytics-modal-close" onClick={onClose}>×</button>
          <h3>Details for {format(parseISO(date), 'MMMM d, yyyy')}</h3>
          {seriesName && <p>Context: <strong>{seriesName}</strong></p>}
          
          <h4>Alerts on this day ({alerts.length}):</h4>
          {alerts.length > 0 ? (
            <div className="modal-alerts-table-wrapper">
              <table>
                <thead>
                  <tr><th>Time</th><th>Mode</th><th>Camera</th><th>Reason</th></tr>
                </thead>
                <tbody>
                  {alerts.slice(0,15).map((alert, idx) => (
                    <tr key={idx}>
                      <td>{format(parseISO(alert.event_timestamp_utc), 'HH:mm:ss')}</td>
                      <td>{alert.mode}</td>
                      <td>{alert.camera_display_name || 'N/A'}</td>
                      <td>{alert.alert_reason}</td>
                    </tr>
                  ))}
                  {alerts.length > 15 && <tr><td colSpan="4">...and {alerts.length - 15} more</td></tr>}
                </tbody>
              </table>
            </div>
          ) : <p>No specific alerts for this selection and filters on this day.</p>}

          <h4>Hourly Breakdown (Filtered Alerts):</h4>
          {hourlyStats && hourlyStats.length > 0 && hourlyStats.some(h => h.total > 0) ? ( // Check if any hour has data
            <ResponsiveContainer width="100%" height={200}>
                <BarChart data={hourlyStats}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hour" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    {/* Only show bars if they have data or keep them for consistency */}
                    <Bar dataKey="audio" name={ALERT_CONFIG.AUDIO_NEGATIVE.label} stackId="a" fill="#82ca9d" />
                    <Bar dataKey="bounce" name={ALERT_CONFIG.BOUNCE_OCCURRED.label} stackId="a" fill="#ffc658" />
                    <Bar dataKey="idle" name={ALERT_CONFIG.LONG_IDLE.label} stackId="a" fill="#FA8072" />
                    {/* <Bar dataKey="total" name="Total Alerts" fill="#8884d8" /> // Optional: if you want a total bar too */}
                </BarChart>
            </ResponsiveContainer>
          ) : <p>No hourly alert data for this selection on this day.</p>}
        </div>
      </div>
    );
  };

  return (
    <div className="analytics-dashboard-page">
      <div className="analytics-header">
        <h1 className="analytics-title">Analytics Dashboard</h1>
        <p className="analytics-subtitle">Key metrics and trends from Consolidated Alerts.</p>
      </div>

      <div className="filters-container">
        <div className="filter-item">
          <label htmlFor="analytics-start-date">Start Date:</label>
          <input type="date" id="analytics-start-date" value={startDate ? format(startDate, 'yyyy-MM-dd') : ''} onChange={(e) => setStartDate(e.target.value ? startOfDay(parseISO(e.target.value)) : null)} disabled={isLoading || isProcessing} />
        </div>
        <div className="filter-item">
          <label htmlFor="analytics-end-date">End Date:</label>
          <input type="date" id="analytics-end-date" value={endDate ? format(endDate, 'yyyy-MM-dd') : ''} onChange={(e) => setEndDate(e.target.value ? endOfDay(parseISO(e.target.value)) : null)} min={startDate ? format(startDate, 'yyyy-MM-dd') : undefined} disabled={isLoading || isProcessing} />
        </div>
        <div className="filter-item">
          <label htmlFor="analytics-granularity">Granularity:</label>
          <select id="analytics-granularity" value={granularity} onChange={(e) => setGranularity(e.target.value)} disabled={isLoading || isProcessing} >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
         <div className="filter-item">
            <label>Mode(s):</label>
            <Select
                isMulti options={availableAnalyticsModes} value={selectedAnalyticsModes} onChange={setSelectedAnalyticsModes}
                className="react-select-container analytics-filter-select" classNamePrefix="react-select"
                placeholder="All Modes" isDisabled={isLoading || isProcessing || availableAnalyticsModes.length === 0}
            />
        </div>
        <div className="filter-item">
            <label>Camera(s):</label>
            <Select
                isMulti options={availableAnalyticsCameras} value={selectedAnalyticsCameras} onChange={setSelectedAnalyticsCameras}
                className="react-select-container analytics-filter-select" classNamePrefix="react-select"
                placeholder="All Cameras" isDisabled={isLoading || isProcessing || availableAnalyticsCameras.length === 0}
            />
        </div>
      </div>

      {error && !toast.isActive("apiErrorToast") && <p className="error-message-analytics">{(() => { toast.error(error, {toastId: "apiErrorToast"}); return error; })()}</p>}
      
      <div className="charts-grid">
        <div className="chart-card">
          <h3 className="chart-title">Critical Alert Trends</h3>
          {renderChart("Alert Trends", alertTrendData,
            <LineChart data={alertTrendData} margin={{ top: 5, right: 20, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tickFormatter={formatDateTick} onClick={(data) => data && data.value && handleChartPointClick(data.value, "Overall Trend for selected filters")} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="audio_negative_count" name={ALERT_CONFIG.AUDIO_NEGATIVE.label} stroke="#8884d8" activeDot={{ r: 6, onClick: (props) => props.payload && handleChartPointClick(props.payload.date, ALERT_CONFIG.AUDIO_NEGATIVE.label) }} connectNulls={false} />
              <Line type="monotone" dataKey="bounce_occurred_count" name={ALERT_CONFIG.BOUNCE_OCCURRED.label} stroke="#82ca9d" activeDot={{ r: 6, onClick: (props) => props.payload && handleChartPointClick(props.payload.date, ALERT_CONFIG.BOUNCE_OCCURRED.label) }} connectNulls={false} />
              <Line type="monotone" dataKey="long_idle_event_count" name={ALERT_CONFIG.LONG_IDLE.label} stroke="#FA8072" activeDot={{ r: 6, onClick: (props) => props.payload && handleChartPointClick(props.payload.date, ALERT_CONFIG.LONG_IDLE.label) }} connectNulls={false} />
            </LineChart>
          )}
        </div>

        <div className="chart-card">
          <h3 className="chart-title">Average Idle Time Trend (Minutes)</h3>
          {renderChart("Average Idle Time", idleAverageData,
            <LineChart data={idleAverageData} margin={{ top: 5, right: 20, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tickFormatter={formatDateTick} onClick={(data) => data && data.value && handleChartPointClick(data.value, "Average Idle Time")} />
              <YAxis dataKey="average_idle_minutes" />
              <Tooltip formatter={(value) => `${parseFloat(value).toFixed(1)} min`} />
              <Legend />
              <Line type="monotone" dataKey="average_idle_minutes" name="Avg. Idle (min)" stroke="#ffc658" activeDot={{ r: 6, onClick: (props) => props.payload && handleChartPointClick(props.payload.date, "Average Idle Time") }} connectNulls={false}/>
            </LineChart>
          )}
        </div>
        
        <div className="chart-card">
          <h3 className="chart-title">Idle Event Duration Distribution</h3>
          {renderChart("Idle Distribution", idleDistributionData,
            <BarChart data={idleDistributionData} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" allowDecimals={false}/>
              <YAxis dataKey="bucket" type="category" width={80} interval={0} />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" name="Event Count" fill="#4682B4" />
            </BarChart>
          , "bucket")}
        </div>

        <div className="chart-card">
          <h3 className="chart-title">Top Cameras by Alert Volume</h3>
          {renderChart("Top Cameras", topCamerasData,
            <BarChart layout="vertical" data={topCamerasData} margin={{ top: 5, right: 30, left: 120, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" allowDecimals={false} />
              <YAxis dataKey="camera_display_name" type="category" width={120} interval={0} />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" name="Alert Count" fill="#20B2AA" />
            </BarChart>
          , "camera_display_name")}
        </div>

        <div className="chart-card">
          <h3 className="chart-title">Alerts by Hour of Day (by Mode)</h3>
          {renderChart("Alerts by Hour", alertsByHourData,
            <BarChart data={alertsByHourData} margin={{ top: 5, right: 20, left: -15, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="hour" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Bar dataKey={ALERT_CONFIG.AUDIO_NEGATIVE.label} stackId="a" name={ALERT_CONFIG.AUDIO_NEGATIVE.label} fill="#8884d8" />
              <Bar dataKey={ALERT_CONFIG.BOUNCE_OCCURRED.label} stackId="a" name={ALERT_CONFIG.BOUNCE_OCCURRED.label} fill="#82ca9d" />
              <Bar dataKey={ALERT_CONFIG.LONG_IDLE.label} stackId="a" name={ALERT_CONFIG.LONG_IDLE.label} fill="#FA8072" />
            </BarChart>
          , "hour")}
        </div>

        <div className="chart-card">
          <h3 className="chart-title">Gemini Token Usage</h3>
          {processedTokenData.chartData.length === 0 ? (
            <div className="no-data-message-analytics">No token usage data available.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '20px' }}>
              <div style={{ flex: '1' }}>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={processedTokenData.chartData} margin={{ top: 20, right: 30, left: 20, bottom: 50 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="model" 
                      angle={-45} 
                      textAnchor="end" 
                      height={80}
                      fontSize={12}
                    />
                    <YAxis allowDecimals={false} />
                    <Tooltip 
                      formatter={(value, name) => [`${value.toLocaleString()} tokens`, name]}
                    />
                    <Legend />
                    <Bar dataKey="input_tokens" name="Input Tokens" fill="#3B82F6" />
                    <Bar dataKey="output_tokens" name="Output Tokens" fill="#10B981" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div style={{ flex: '1', padding: '20px' }}>
                <div style={{ marginBottom: '15px' }}>
                  <h4 style={{ margin: '0 0 10px 0', color: '#1E293B', fontSize: '16px' }}>Token Usage Summary</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#64748B' }}>Total Input Tokens:</span>
                      <strong style={{ color: '#3B82F6' }}>{processedTokenData.totalInputTokens.toLocaleString()}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#64748B' }}>Total Output Tokens:</span>
                      <strong style={{ color: '#10B981' }}>{processedTokenData.totalOutputTokens.toLocaleString()}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px', borderTop: '1px solid #E2E8F0' }}>
                      <span style={{ color: '#64748B' }}>Total Tokens Used:</span>
                      <strong style={{ color: '#0F172A' }}>{processedTokenData.totalTokens.toLocaleString()}</strong>
                    </div>
                    {processedTokenData.chartData.map((model, index) => (
                      <div key={model.model} style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        paddingTop: index === 0 ? '8px' : '4px',
                        borderTop: index === 0 ? '1px solid #E2E8F0' : 'none'
                      }}>
                        <span style={{ color: '#64748B', fontSize: '14px' }}>{model.model}:</span>
                        <strong style={{ color: '#6B7280', fontSize: '14px' }}>{model.total_tokens.toLocaleString()}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

      </div>
      <DailyDetailModal show={showDetailModal} onClose={() => setShowDetailModal(false)} content={detailModalContent} />
    </div>
  );
};

export default AnalyticsDashboard;